import { Injectable } from '@angular/core';
import { timer, Subject } from 'rxjs';
import { delay } from 'rxjs/operators';
import { TourGuideContent } from '@app/util/tour-guide-content.util';
import { GuidedTourService, TourStep, Orientation } from 'ngx-guided-tour';
import { PopUpService } from '@app/services/pop-up.service';
import { Router } from '@angular/router';
import { Messages } from '@app/util/messages.util';
import { ToastService } from '@app/services/toast.service';
import { I18nService } from '@app/services/i18n/i18n.service';
import { CommonFunctionalitiesService } from './common-functionalities.service';
@Injectable({
  providedIn: 'root'
})

export class TourGuideService {

  /****************** Variable declaration ******************/
  public isStartTour: boolean = false; //set flag to start the tour
  public clickTriggered: boolean = false;
  public dashboardIndex: number = 0;
  currentStepIndex = 0;
  private dataSubject = new Subject<any>();
  data$ = this.dataSubject.asObservable();
  dashboardTab: string = '';
  private currentTourSteps: TourStep[] = []; // Track current tour steps

  /**
   * Constructor
   * @param gdTourService 
   * @param popUpService 
   * @param router 
   * @param ts 
   * @param i18n 
   * @param toast 
   */
  constructor(private gdTourService: GuidedTourService,
    private popUpService: PopUpService,
    private router: Router,
    public ts: ToastService,
    public i18n: I18nService, private toast: ToastService,
    private cf: CommonFunctionalitiesService) {
    window.addEventListener('popstate', () => {
      this.isStartTour = false;
      this.gdTourService.resetTour();
    });
  }

  /**
   * triggerStartTour function is used to trigger the tour using the guided tour library
   * @param steps 
   */
  goTonextStep(): void {
    this.currentStepIndex++;
  }

  /** Function to move to the previous step */
  goToPreviousStep(): void {
    this.currentStepIndex--;
  }

  /**
  * Navigates to a specific url within the workflow tour.
  * @param {number} index - The index of the step to navigate to.
  */
  navigateToStep(index: number) {
    if (index <= 0 && index < this.workflowsSteps.length) {
      // If the index is valid within the range of available steps.
      const step = this.workflowsSteps[index];
      this.router.navigate(['/workflows']); // Adjust the URL structure as needed
    }
    else {
      this.router.navigate(['/workflow-run/2']); // Adjust the URL structure as needed
    }
  }

  /** trigger the tour guide service */
  triggerStartTour(steps: any) {
    this.currentTourSteps = steps; // Track which tour is active
    this.gdTourService.startTour({
      tourId: 'tour',
      useOrb: false,
      steps,
      preventBackdropFromAdvancing: true,
      completeCallback: () => {
        this.startPageTour(false)
        this.handleClick(false);
        this.toast.closeToast(); //close the toaster if any open after completion of the tour guide
        this.cf.prodTourSubject.next(false);
        // Close dependent checks dialog if that tour just completed
        if (this.currentTourSteps === this.dependentChecksSteps) {
          this.dataSubject.next({ cancelConfig: true });
        }
        this.currentTourSteps = []; // Clear after completion
      },
      skipCallback: (stepSkippedOn: number) => {
        this.startPageTour(false)
        this.onSkip();
        this.handleClick(false);
        this.cf.prodTourSubject.next(false);
        // Close dependent checks dialog if that tour was skipped
        if (this.currentTourSteps === this.dependentChecksSteps) {
          this.dataSubject.next({ cancelConfig: true });
        }
        this.currentTourSteps = []; // Clear after skip
      },
    })
  }

  /**
   * onSkip function is used to implement the closing and skipping the steps that are being called during the tour
   */
  onSkip() {
    this.dataSubject.next({ operation: 'skip' })
    this.dataSubject.next({ operation: 'close' }) // used for closing of dialog boxes that are being opened during the tour
    this.tourClass('productTourId', '', 'remove'); //This is basically used for removing the class that are being called during the tour
    this.simulateClickOnElement(TourGuideContent.clickId) //this is used for closing the runner popup in configbuilder page that has been opened during the tour
  }

  /**
   * Welcome popup product tour
   */
  productTour() {
    this.cf.prodTourSubject.next(true);
    this.popUpService.openPopupDialog(
      this.i18n.getString(TourGuideContent.welcomePopUp.title),
      this.i18n.getString(TourGuideContent.welcomePopUp.content),
      this.i18n.getString(TourGuideContent.welcomePopUp.skip),
      this.i18n.getString(TourGuideContent.welcomePopUp.start)
    ).subscribe((result: string) => {
      if (result === 'rightBtn') {
        this.triggerStartTour(this.navSteps);
      } else if (result === 'leftBtn') {
        this.cf.prodTourSubject.next(false);
      }
    });
  }

  /**********  Navbar tour steps **********/
  navSteps: TourStep[] = [
    {
      title: '',
      content: this.i18n.getString(TourGuideContent.navbarContent.dashboard.content),
      selector: '#navbar-0',
      orientation: Orientation.BottomLeft,
    },
    {
      title: '',
      content: this.i18n.getString(TourGuideContent.navbarContent.featureWf.content),
      selector: '#navbar-1',
      orientation: Orientation.Bottom,
    },
    {
      title: '',
      content: this.i18n.getString(TourGuideContent.navbarContent.wfQueueMgr.content),
      selector: '#navbar-2',
      orientation: Orientation.Bottom,
    },
    {
      title: '',
      content: this.i18n.getString(TourGuideContent.navbarContent.configBuilder.content),
      selector: '#navbar-3',
      orientation: Orientation.Bottom,
    },
    {
      title: '',
      content: this.i18n.getString(TourGuideContent.navbarContent.githubAction.content),
      selector: '#navbar-4',
      orientation: Orientation.Bottom,
    },
    {
      title: '',
      content: this.i18n.getString(TourGuideContent.navbarContent.workflow.content),
      selector: '#navbar-5',
      orientation: Orientation.Bottom,
    },
    {
      title: '',
      content: this.i18n.getString(TourGuideContent.navbarContent.admin.content),
      selector: '#navbar-6',
      orientation: Orientation.Bottom,
    },
    {
      title: '',
      content: this.i18n.getString(TourGuideContent.navbarContent.help.content),
      selector: '#navbar-7',
      orientation: Orientation.Bottom,
    },
  ]

/**********  KPI Dashboard steps **********/
  kpiDashboardSteps: TourStep[] = [
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.kpidashboard.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.kpidashboard.content),
      selector: '',
      orientation: Orientation.Center,
    },
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.kpidashboard.card.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.kpidashboard.card.content),
      selector: `#${TourGuideContent.DashboardContent.kpidashboard.card.id}`,
      orientation: Orientation.Right,
    }
  ]

  /**********  Distribution steps **********/
  distributionSteps: TourStep[] = [
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.distribution.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.distribution.content),
      selector: '',
      orientation: Orientation.Center,
    },
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfDuration.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfDuration.content),
      selector: `#${TourGuideContent.DashboardContent.distribution.wfDuration.id}`,
      orientation: Orientation.Right,
      action: () => this.dataSubject.next({ operation: 'open' }),
    },
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfExec.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfExec.content),
      selector: `#${TourGuideContent.DashboardContent.distribution.wfExec.id}`,
      orientation: Orientation.Right,
      action: () => this.dataSubject.next({ operation: 'close' })
    },
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfSuccess.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfSuccess.content),
      selector: `#${TourGuideContent.DashboardContent.distribution.wfSuccess.id}`,
      orientation: Orientation.Right,
    },
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfFailed.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfFailed.content),
      selector: `#${TourGuideContent.DashboardContent.distribution.wfFailed.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.scrollToView(TourGuideContent.DashboardContent.distribution.wfFailed.id);
      },
    },
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfQueued.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfQueued.content),
      selector: `#${TourGuideContent.DashboardContent.distribution.wfQueued.id}`,
      orientation: Orientation.Right,
    },
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfInProg.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfInProg.content),
      selector: `#${TourGuideContent.DashboardContent.distribution.wfInProg.id}`,
      orientation: Orientation.Right,
    },
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfAborted.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfAborted.content),
      selector: `#${TourGuideContent.DashboardContent.distribution.wfAborted.id}`,
      orientation: Orientation.Left,
    },
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfStatusDist.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.distribution.wfStatusDist.content),
      selector: `#${TourGuideContent.DashboardContent.distribution.wfStatusDist.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.scrollToView('dash-wfStatusDistribution');
      },
    },

  ]

  /**********  summary tab tour steps **********/
  summarySteps: TourStep[] = [
    /** Tour step 1 */
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.summary.summaryDropDown.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.summary.summaryDropDown.content),
      selector: `#${TourGuideContent.DashboardContent.summary.summaryDropDown.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.dataSubject.next({ operation: 'open' }),
          this.tourClass('productTourId', 'wfQueDropdowns', 'add')
        this.scrollToView(TourGuideContent.DashboardContent.summary.summaryDropDown.id);
      },
      closeAction: () => {
        this.dataSubject.next({ operation: 'close' })
        this.tourClass('productTourId', 'wfQueDropdowns', 'remove');
        this.scrollToView(TourGuideContent.DashboardContent.summary.summaryDropDown.id);
      }
    },
    /** Tour step 2 */
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.summary.summaryChart.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.summary.summaryChart.content),
      selector: `#${TourGuideContent.DashboardContent.summary.summaryChart.id}`,
      orientation: Orientation.Right,
      action: () => {
        this.scrollToView(TourGuideContent.DashboardContent.summary.summaryChart.id);
      },

    }
  ]
  /**********  license tab tour steps **********/
  licenseSteps: TourStep[] = [
    /** Tour step 1 */
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.licenses.mostUsedWfTable.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.licenses.mostUsedWfTable.content),
      selector: `#${TourGuideContent.DashboardContent.licenses.mostUsedWfTable.id}`,
      orientation: Orientation.Right,
      action: () => {
        this.scrollToView(TourGuideContent.DashboardContent.licenses.mostUsedWfTable.id);
      }
    },
    /** Tour step 2 */
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.licenses.refresh.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.licenses.refresh.content),
      selector: `#${TourGuideContent.DashboardContent.licenses.refresh.id}`,
      orientation: Orientation.Bottom,
      action: () => {
        this.scrollToView(TourGuideContent.DashboardContent.licenses.refresh.id);
      }
    },
    /** Tour step 3 */
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.licenses.licStatusTable.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.licenses.licStatusTable.content),
      selector: `#${TourGuideContent.DashboardContent.licenses.licStatusTable.id}`,
      orientation: Orientation.Bottom,
      action: () => {
        this.scrollToView(TourGuideContent.DashboardContent.licenses.licStatusTable.id);
      }
    },
    /** Tour step 4 */
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.licenses.utilization.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.licenses.utilization.content),
      selector: `#${TourGuideContent.DashboardContent.licenses.utilization.id}`,
      orientation: Orientation.Bottom,
      action: () => {
        this.scrollToView(TourGuideContent.DashboardContent.licenses.utilization.id);
      }
    },
  ]
  /**********  errorDiagnostics tab tour steps **********/
  errorDiagnosticsSteps: TourStep[] = [
    /** Tour step 1 */
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.errorDiagnostics.errorDiagnosticsbargraphData.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.errorDiagnostics.errorDiagnosticsbargraphData.content),
      selector: `#${TourGuideContent.DashboardContent.errorDiagnostics.errorDiagnosticsbargraphData.id}`,
      orientation: Orientation.Right,
      action: () => {
        this.scrollToView(TourGuideContent.DashboardContent.errorDiagnostics.errorDiagnosticsbargraphData.id);
      }
    },
    /** Tour step 2 */
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.errorDiagnostics.errorDiagnosticsLinechartData.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.errorDiagnostics.errorDiagnosticsLinechartData.content),
      selector: `#${TourGuideContent.DashboardContent.errorDiagnostics.errorDiagnosticsLinechartData.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.scrollToView(TourGuideContent.DashboardContent.errorDiagnostics.errorDiagnosticsLinechartData.id);
      }
    },
    /** Tour step 3 */
    {
      title: this.i18n.getString(TourGuideContent.DashboardContent.errorDiagnostics.errorDiagnosticstableData.title),
      content: this.i18n.getString(TourGuideContent.DashboardContent.errorDiagnostics.errorDiagnosticstableData.content),
      selector: `#${TourGuideContent.DashboardContent.errorDiagnostics.errorDiagnosticstableData.id}`,
      orientation: Orientation.TopLeft,
      action: () => {
        this.scrollToView(TourGuideContent.DashboardContent.errorDiagnostics.errorDiagnosticstableData.id);
      },
    }
  ]

  /**********  Feature workflow tour steps **********/
  featureWfSteps: TourStep[] = [
    /** Tour step 1 */
    {
      title: this.i18n.getString(TourGuideContent.navbarContent.featureWf.title),
      content: this.i18n.getString(TourGuideContent.navbarContent.featureWf.content),
      selector: '',
      orientation: Orientation.Center,
    },
    /** Tour step 2 */
    {
      title: this.i18n.getString(TourGuideContent.featureWfContent.charts.dropdowns.title),
      content: this.i18n.getString(TourGuideContent.featureWfContent.charts.dropdowns.content),
      selector: `#${TourGuideContent.wfQueueMgr.dropDown.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.dataSubject.next({ operation: 'open' });
        this.tourClass('productTourId', 'wf-configw', 'add');
        this.scrollToView(TourGuideContent.wfQueueMgr.dropDown.id);
      },
      closeAction: () => {
        this.dataSubject.next({ operation: 'close' })
        this.tourClass('productTourId', 'wf-configw', 'remove')
        this.scrollToView(TourGuideContent.featureWfContent.charts.dropdowns.id);
      },
    },
    /** Tour step 3 */
    {
      title: this.i18n.getString(TourGuideContent.featureWfContent.charts.deploymentFreq.title),
      content: this.i18n.getString(TourGuideContent.featureWfContent.charts.deploymentFreq.content),
      selector: '#wf-deployment',
      orientation: Orientation.Right,
      action: () => {
        this.scrollToView('wf-deployment');
      },
    },
    /** Tour step 4 */
    {
      title: this.i18n.getString(TourGuideContent.featureWfContent.charts.changeFailRate.title),
      content: this.i18n.getString(TourGuideContent.featureWfContent.charts.changeFailRate.content),
      selector: `#${TourGuideContent.featureWfContent.charts.changeFailRate.id}`,
      orientation: Orientation.Right,
      action: () => {
        this.scrollToView(TourGuideContent.featureWfContent.charts.changeFailRate.id);
      },
    },
    /** Tour step 5 */
    {
      title: this.i18n.getString(TourGuideContent.featureWfContent.charts.avgLeadTime.title),
      content: this.i18n.getString(TourGuideContent.featureWfContent.charts.avgLeadTime.content),
      selector: `#${TourGuideContent.featureWfContent.charts.avgLeadTime.id}`,
      orientation: Orientation.Right,
      action: () => {
        this.scrollToView(TourGuideContent.featureWfContent.charts.avgLeadTime.id);
      },
    },
    /** Tour step 6 */
    {
      title: this.i18n.getString(TourGuideContent.featureWfContent.charts.avgRestoreTime.title),
      content: this.i18n.getString(TourGuideContent.featureWfContent.charts.avgRestoreTime.content),
      selector: `#${TourGuideContent.featureWfContent.charts.avgRestoreTime.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.scrollToView(TourGuideContent.featureWfContent.charts.avgRestoreTime.id);
      },
    },
    /** Tour step 7 */
    {
      title: this.i18n.getString(TourGuideContent.featureWfContent.featureWfTable.wfDetail.title),
      content: this.i18n.getString(TourGuideContent.featureWfContent.featureWfTable.wfDetail.content),
      selector: `#${TourGuideContent.featureWfContent.featureWfTable.wfDetail.id}`,
      orientation: Orientation.Bottom,
      action: () => {
        this.scrollToView(TourGuideContent.featureWfContent.featureWfTable.wfDetail.id);
      }
    },
    /** Tour step 8 */
    {
      title: this.i18n.getString(TourGuideContent.featureWfContent.featureWfTable.wfTableExpand.title),
      content: this.i18n.getString(TourGuideContent.featureWfContent.featureWfTable.wfTableExpand.content),
      selector: `#${TourGuideContent.featureWfContent.featureWfTable.wfTableExpand.id}`,
      orientation: Orientation.Right,
      action: () => {
        this.simulateClickOnElement('wf-featureId');
        this.scrollToView(TourGuideContent.featureWfContent.featureWfTable.wfTableExpand.id)
      },
      closeAction: () => {
        this.simulateClickOnElement('wf-featureId')
      },
    },
    /** Tour step 9 */
    {
      title: this.i18n.getString(TourGuideContent.featureWfContent.featureWfTable.qualityGate_sum.title),
      content: this.i18n.getString(TourGuideContent.featureWfContent.featureWfTable.qualityGate_sum.content),
      selector: '#wf-qualityGates',
      orientation: Orientation.Top,
      action: () => {
        this.simulateClickOnElement('wf-featureId');
        this.simulateClickOnElement('wf-featureTimeLineId')
        this.scrollToView('wf-featureTimeLineId');

      },
      closeAction: () => {
        this.simulateClickOnElement('wf-featureId');
        this.scrollToView('wf-featureTimeLineId');
      }
    },
  ]

  /**********  Workflow queue manager tour steps **********/
  /** Tour step 1 */
  wfQueueMgrSteps: TourStep[] = [
    {
      title: this.i18n.getString(TourGuideContent.navbarContent.wfQueueMgr.title),
      content: this.i18n.getString(TourGuideContent.navbarContent.wfQueueMgr.content),
      selector: '',
      orientation: Orientation.Center,
    },
    /** Tour step 2*/
    {
      title: this.i18n.getString(TourGuideContent.wfQueueMgr.liveUpd.title),
      content: this.i18n.getString(TourGuideContent.wfQueueMgr.liveUpd.content),
      selector: `#${TourGuideContent.wfQueueMgr.liveUpd.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.scrollToView(TourGuideContent.wfQueueMgr.liveUpd.id);
      },
    },
    /** Tour step 3 */
    {
      title: this.i18n.getString(TourGuideContent.wfQueueMgr.dropDown.title),
      content: this.i18n.getString(TourGuideContent.wfQueueMgr.dropDown.content),
      selector: `#${TourGuideContent.wfQueueMgr.dropDown.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.dataSubject.next({ operation: 'open' }),
          this.tourClass('productTourId', 'wfQueDropdowns', 'add')
        this.scrollToView(TourGuideContent.wfQueueMgr.dropDown.id);
      },
      closeAction: () => {
        this.dataSubject.next({ operation: 'close' })
        this.tourClass('productTourId', 'wfQueDropdowns', 'remove');
        this.scrollToView(TourGuideContent.wfQueueMgr.dropDown.id);
      }
    },
    /** Tour step 4 */
    {
      title: this.i18n.getString(TourGuideContent.wfQueueMgr.charts.waitLic.title),
      content: this.i18n.getString(TourGuideContent.wfQueueMgr.charts.waitLic.content),
      selector: `#${TourGuideContent.wfQueueMgr.charts.waitLic.id}`,
      orientation: Orientation.Right,
    },
    /** Tour step 5 */
    {
      title: this.i18n.getString(TourGuideContent.wfQueueMgr.charts.waitDep.title),
      content: this.i18n.getString(TourGuideContent.wfQueueMgr.charts.waitDep.content),
      selector: `#${TourGuideContent.wfQueueMgr.charts.waitDep.id}`,
      orientation: Orientation.Right,
    },
    /** Tour step 6 */
    {
      title: this.i18n.getString(TourGuideContent.wfQueueMgr.charts.waitAgent.title),
      content: this.i18n.getString(TourGuideContent.wfQueueMgr.charts.waitAgent.content),
      selector: `#${TourGuideContent.wfQueueMgr.charts.waitAgent.id}`,
      orientation: Orientation.Right,
    },
    /** Tour step 7 */
    {
      title: this.i18n.getString(TourGuideContent.wfQueueMgr.charts.runningWf.title),
      content: this.i18n.getString(TourGuideContent.wfQueueMgr.charts.runningWf.content),
      selector: `#${TourGuideContent.wfQueueMgr.charts.runningWf.id}`,
      orientation: Orientation.Left,
    },
    /** Tour step 8 */
    {
      title: this.i18n.getString(TourGuideContent.wfQueueMgr.wfQueTable.wfQueueTable.title),
      content: this.i18n.getString(TourGuideContent.wfQueueMgr.wfQueTable.wfQueueTable.content),
      selector: `#${TourGuideContent.wfQueueMgr.wfQueTable.wfQueueTable.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.scrollToView(TourGuideContent.wfQueueMgr.wfQueTable.wfQueueTable.id)
      }
    },
    /** Tour step 9 */
    {
      title: this.i18n.getString(TourGuideContent.wfQueueMgr.wfQueTable.wfTableExpand.title),
      content: this.i18n.getString(TourGuideContent.wfQueueMgr.wfQueTable.wfTableExpand.content),
      selector: `#${TourGuideContent.wfQueueMgr.wfQueTable.wfTableExpand.id}`,
      orientation: Orientation.TopLeft,
      action: () => {
        this.simulateClickOnElement('wf-expand');
      },
      closeAction: () => {
        this.simulateClickOnElement('wf-expand');
        this.dashboardIndex = 1
      }
    },
    /** Tour step 10 */
    {
      title: this.i18n.getString(TourGuideContent.wfQueueMgr.wfQueTable.wfQueueDetail.title),
      content: this.i18n.getString(TourGuideContent.wfQueueMgr.wfQueTable.wfQueueDetail.content),
      selector: `#${TourGuideContent.wfQueueMgr.wfQueTable.wfQueueDetail.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.simulateClickOnElement('wf-expand');
        this.dashboardIndex = 0
      },
      closeAction: () => {
        this.simulateClickOnElement('wf-expand')
      },
    },

    /** Tour step 11 */
    {
      title: this.i18n.getString(TourGuideContent.wfQueueMgr.wfQueTable.priority.title),
      content: this.i18n.getString(TourGuideContent.wfQueueMgr.wfQueTable.priority.content),
      selector: `#${TourGuideContent.wfQueueMgr.wfQueTable.priority.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.simulateClickOnElement('wf-expand');
        this.dashboardIndex = 1
      },
      closeAction: () => {
        this.simulateClickOnElement('wf-expand');
      },
    },
    /** Tour step 12 */
    {
      title: this.i18n.getString(TourGuideContent.wfQueueMgr.wfQueTable.action.title),
      content: this.i18n.getString(TourGuideContent.wfQueueMgr.wfQueTable.action.content),
      selector: `#${TourGuideContent.wfQueueMgr.wfQueTable.action.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.simulateClickOnElement('wf-expand');
      },
      closeAction: () => {
        this.simulateClickOnElement('wf-expand');
      },
    },
  ]

  /**********  Config builder workflow tour steps **********/
  /** Tour step 1 */
  configBuilderWfSteps: TourStep[] = [
    {
      title: this.i18n.getString(TourGuideContent.navbarContent.configBuilder.title),
      content: this.i18n.getString(TourGuideContent.navbarContent.configBuilder.content),
      selector: '',
      orientation: Orientation.Center,
    },
    /** Tour step 2 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.configTable.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.configTable.content),
      selector: `#${TourGuideContent.configBuilder.workflow.configTable.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.simulateClickOnElement(TourGuideContent.configBuilder.workflow.saveBtn.id)
        this.scrollToView(TourGuideContent.configBuilder.workflow.configTable.id);
      }
    },
    /** Tour step 3 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.configTableEdit.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.configTableEdit.content),
      selector: `#${TourGuideContent.configBuilder.workflow.configTableEdit.id}`,
      orientation: Orientation.TopRight,
      action: () => {
        this.scrollToView(TourGuideContent.configBuilder.workflow.configTable.id);
      }
    },
    /** Tour step 4 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.configTableDelete.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.configTableDelete.content),
      selector: `#${TourGuideContent.configBuilder.workflow.configTableDelete.id}`,
      orientation: Orientation.TopRight,
    },
    /** Tour step 5 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.btn.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.btn.content),
      selector: `#${TourGuideContent.configBuilder.workflow.btn.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.scrollToView(TourGuideContent.configBuilder.workflow.btn.id);
      },
    },
    /** Tour step 6 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.addConfigName.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.addConfigName.content),
      selector: `#${TourGuideContent.configBuilder.workflow.addConfigName.id}`,
      orientation: Orientation.Bottom,
      action: () => {
        // this.dataSubject.next('close')
        this.simulateClickOnElement(TourGuideContent.configBuilder.workflow.btn.id);
      },
      closeAction: () => {
        this.simulateClickOnElement('wf-cancel-btn');
        timer(100).pipe(delay(100)).subscribe(() => {
          this.simulateClickOnElement('cnfg-cancel');
          this.scrollToView(TourGuideContent.configBuilder.workflow.btn.id)
        })
      },
    },
    /** Tour step 7 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.selectConfig.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.selectConfig.content),
      selector: `#${TourGuideContent.configBuilder.workflow.selectConfig.id}`,
      orientation: Orientation.Bottom,
      highlightPadding: 10,
      action: () => {
        // this.dataSubject.next({ page: 'config', operation: true });
        this.dataSubject.next(true)
        this.dataSubject.next({ page: 'config' })
        this.scrollToView(TourGuideContent.configBuilder.workflow.selectConfig.id)
      },
    },
    // /** Tour step 2 */
    // {
    //   title: this.i18n.getString(TourGuideContent.featureWfContent.charts.dropdowns.title),
    //   content: this.i18n.getString(TourGuideContent.featureWfContent.charts.dropdowns.content),
    //   selector: `#${TourGuideContent.configBuilder.workflow.selectConfig.id}`,
    //   orientation: Orientation.Bottom,
    // },
    /** Tour step 8 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.wfDetailsTab.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.wfDetailsTab.content),
      selector: `#${TourGuideContent.configBuilder.workflow.wfDetailsTab.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.scrollToView(TourGuideContent.configBuilder.workflow.wfDetailsTab.id);
        this.dashboardIndex = 0;
        this.dataSubject.next(false);
        this.tourClass('productTourId', 'tourStepCard', 'add')
      },
      closeAction: () => {
        this.tourClass('productTourId', 'tourStepCard', 'remove')
        this.simulateClickOnElement(TourGuideContent.configBuilder.workflow.wfDetailsTab.id);
      }
    },
    /** Tour step 9 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.tabContainer.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.tabContainer.content),
      selector: `#${TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.scrollToView(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.id);
        this.dashboardIndex = 1;
        this.tourClass('productTourId', 'tourStepCard', 'add')
      },
      closeAction: () => {
        this.simulateClickOnElement(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.id);
        this.tourClass('productTourId', 'tourStepCard', 'remove')
      }
    },
    // /** Tour step 10 */
    // {
    //   title: this.i18n.getString(TourGuideContent.configBuilder.workflow.runnerDetailEdit.title),
    //   content: this.i18n.getString(TourGuideContent.configBuilder.workflow.runnerDetailEdit.content),
    //   selector: `#${TourGuideContent.configBuilder.workflow.runnerDetailEdit.id}`,
    //   orientation: Orientation.TopRight,

    // },
    // /** Tour step 11 */
    // {
    //   title: this.i18n.getString(TourGuideContent.configBuilder.workflow.runnerDetail.title),
    //   content: this.i18n.getString(TourGuideContent.configBuilder.workflow.runnerDetail.content),
    //   selector: `#${TourGuideContent.configBuilder.workflow.runnerDetail.id}`,
    //   orientation: Orientation.Top,
    //   action: () => {
    //     this.simulateClickOnElement(TourGuideContent.configBuilder.workflow.runnerDetailEdit.id)
    //     timer(500).pipe(delay(500)).subscribe(() => {
    //       this.dataSubject.next({ operation: 'open' });
    //     })
    //     this.tourClass('productTourId', 'runnerdetail', 'add')
    //     this.scrollToView(TourGuideContent.configBuilder.workflow.runnerDetailEdit.id);
    //   },
    //   closeAction: () => {
    //     this.simulateClickOnElement(TourGuideContent.clickId)
    //     this.tourClass('productTourId', 'runnerdetail', 'remove')
    //     this.scrollToView(TourGuideContent.configBuilder.workflow.runnerDetailEdit.id)
    //   }
    // },
    /** Tour step 12 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.content),
      selector: `#${TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.id}`,
      orientation: Orientation.Bottom,
      action: () => {
        this.simulateClickOnElement(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.id);
        this.dashboardIndex = 2;
      },
      closeAction: () => {
        this.simulateClickOnElement(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.id);
      }
    },
    /** Tour step 13 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.checkBox.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.checkBox.content),
      selector: `#${TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.checkBox.id}`,
      orientation: Orientation.Right,
      action: () => this.dataSubject.next({ checkBox: true })
    },
    /** Tour step 14 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.condition.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.condition.content),
      selector: `#${TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.condition.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.dataSubject.next({ operation: 'open' })
        this.dataSubject.next({ checkBox: true }),
          this.tourClass('productTourId', 'config-conditionId', 'add')
      },
      closeAction: () => {
        this.dataSubject.next({ operation: 'close' }),
          this.tourClass('productTourId', 'config-conditionId', 'remove')
      },
    },
    /** Tour step 15 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.inputBox.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.inputBox.content),
      selector: `#${TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.inputBox.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.dataSubject.next({ checkBox: true })
      }
    },
    /** Tour step 16 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.allFail.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.allFail.content),
      selector: `#${TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.allFail.id}`,
      orientation: Orientation.Top,
    },
    /** Tour step 17 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.saveBtn.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.saveBtn.content),
      selector: `#${TourGuideContent.configBuilder.workflow.saveBtn.id}`,
      orientation: Orientation.TopRight,
      action: () => {
        this.dataSubject.next({ checkBox: true, configSave: true })
        this.scrollToView(TourGuideContent.configBuilder.workflow.saveBtn.id);
      },
      closeAction: () => {
        // Scroll back to the table area when going backward
        this.scrollToView(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.id);
      }
    },
  ]

  /**********  Config builder integrated wf tour steps **********/
  configBuilderIntegratedWf: TourStep[] = []

  /**********  Workflows tour steps **********/
  /** Tour step 1 */
  workflowsSteps: TourStep[] = [
    {
      title: this.i18n.getString(TourGuideContent.navbarContent.workflow.title),
      content: this.i18n.getString(TourGuideContent.navbarContent.workflow.content),
      selector: '',
      orientation: Orientation.Center,
    },
    /**
    * Tour step 2
    * @property {string} title - The title of the tour step.
    * @property {string} content - The content or description of the tour step.
    * @property {string} selector - The CSS selector for the element that this step should target.
    * @property {Orientation} orientation - The orientation of the tour step (e.g., Orientation.Right).
    * @property {Function} action - A function to execute when the tour step is reached.
    * @property {Function} closeAction - A function to execute when the tour is closed or completed.
    */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wf.WfDeploymentFrequency.title),
      content: this.i18n.getString(TourGuideContent.workflows.wf.WfDeploymentFrequency.content),
      selector: `#${TourGuideContent.workflows.wf.WfDeploymentFrequency.id}`,
      orientation: Orientation.Right,
      action: () => {
        this.dataSubject.next({
          operation: 'open'
        }), this.tourClass('productTourId', 'wfdropdown', 'add')
      },
      closeAction: () => {
        this.tourClass('productTourId', 'wfdropdown', 'remove')
        this.dataSubject.next({
          operation: 'close'
        })
      }
    },
    /**
    * Tour step 3
    * @property {string} title - The title of the tour step.
    * @property {string} content - The content or description of the tour step.
    * @property {string} selector - The CSS selector for the element that this step should target.
    * @property {Orientation} orientation - The orientation of the tour step (e.g., Orientation.BottomRight).
    * @property {Function} action - A function to execute when the tour step is reached.
    */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wf.wfSearch.title),
      content: this.i18n.getString(TourGuideContent.workflows.wf.wfSearch.content),
      selector: `#${TourGuideContent.workflows.wf.wfSearch.id}`,
      orientation: Orientation.BottomRight,
      action: () => this.dataSubject.next({
        operation: 'close'
      }),
    },
    /**
    * Tour step 4
    * @property {string} title - The title of the tour step.
    * @property {string} content - The content or description of the tour step.
    * @property {string} selector - The CSS selector for the element that this step should target.
    * @property {Orientation} orientation - The orientation of the tour step (e.g., Orientation.Bottom).
    */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wf.wfTable.title),
      content: this.i18n.getString(TourGuideContent.workflows.wf.wfTable.content),
      selector: `#${TourGuideContent.workflows.wf.wfTable.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.dashboardIndex = 0;
      }
    },
    /**
    * Tour step 5
    * @property {string} title - The title of the tour step.
    * @property {string} content - The content or description of the tour step.
    * @property {string} selector - The CSS selector for the element that this step should target.
    * @property {Orientation} orientation - The orientation of the tour step (e.g., Orientation.Bottom).
    */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wf.wfTypeManual.title),
      content: this.i18n.getString(TourGuideContent.workflows.wf.wfTypeManual.content),
      selector: `#${TourGuideContent.workflows.wf.wfTable.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.dashboardIndex = 1;
      }
    },
    /**
    * Tour step 6
    * @property {string} title - The title of the tour step.
    * @property {string} content - The content or description of the tour step.
    * @property {string} selector - The CSS selector for the element that this step should target.
    * @property {number} highlightPadding - The padding (in pixels) for highlighting the element.
    * @property {Orientation} orientation - The orientation of the tour step (e.g., Orientation.BottomRight).
    */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wf.wfPerformance.title),
      content: this.i18n.getString(TourGuideContent.workflows.wf.wfPerformance.content),
      selector: `#${TourGuideContent.workflows.wf.wfPerformance.id}`,
      highlightPadding: 15,
      orientation: Orientation.Top,
    },
    /**
    * Tour step 7
    * @property {string} title - The title of the tour step.
    * @property {string} content - The content or description of the tour step.
    * @property {string} selector - The CSS selector for the element that this step should target.
    * @property {Orientation} orientation - The orientation of the tour step (e.g., Orientation.Left).
    * @property {Function} action - A function to execute when the tour step is reached.
    */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wf.wfView.title),
      content: this.i18n.getString(TourGuideContent.workflows.wf.wfView.content),
      selector: `#${TourGuideContent.workflows.wf.wfView.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.goTonextStep()
      },
    },
    /**
     * Tour step 8
     * @property {string} title - The title of the tour step.
     * @property {string} content - The content or description of the tour step.
     * @property {string} selector - The CSS selector for the element that this step should target.
     * @property {Orientation} orientation - The orientation of the tour step (e.g., Orientation.Bottom).
     * @property {Function} action - A function to execute when the tour step is reached.
     * @property {Function} closeAction - A function to execute when the tour is closed or completed.
    */
    {
      title: this.i18n.getString(TourGuideContent.navbarContent.workflowRun.title),
      content: this.i18n.getString(TourGuideContent.navbarContent.workflowRun.content),
      selector: '',
      orientation: Orientation.Bottom,
      action: () => {
        this.simulateClickOnElement('wf-view');
      },
      closeAction: () => {
        this.navigateToStep(0);
        this.scrollToView(TourGuideContent.workflows.wf.wfView.id);
      }
    },
    /**
    * Tour step 9
    * @property {string} title - The title of the tour step.
    * @property {string} content - The content or description of the tour step.
    * @property {string} selector - The CSS selector for the element that this step should target.
    * @property {Orientation} orientation - The orientation of the tour step (e.g., Orientation.Top).
    * @property {Function} action - A function to execute when the tour step is reached.
    * @property {Function} closeAction - A function to execute when the tour is closed or completed.
    */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wfrun.wfRunDropdowns.title),
      content: this.i18n.getString(TourGuideContent.workflows.wfrun.wfRunDropdowns.content),
      selector: `#${TourGuideContent.wfQueueMgr.dropDown.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.dataSubject.next({ operation: 'open' }),
          this.tourClass('productTourId', 'wfRunDropdowns', 'add')
        this.scrollToView(TourGuideContent.wfQueueMgr.dropDown.id);
        this.navigateToStep(1)
      },
      closeAction: () => {
        this.dataSubject.next({ operation: 'close' })
        this.tourClass('productTourId', 'wfRunDropdowns', 'remove');
        this.scrollToView(TourGuideContent.wfQueueMgr.dropDown.id);
      }
    },
    /** Tour step 10 */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wfrun.wfRunSearch.title),
      content: this.i18n.getString(TourGuideContent.workflows.wfrun.wfRunSearch.content),
      selector: `#${TourGuideContent.workflows.wfrun.wfRunSearch.id}`,
      orientation: Orientation.Right,
      action: () => {
        this.navigateToStep(1);
      },
      closeAction: () => {
        this.simulateClickOnElement('wf-expand');
      }
    },
    /**
    * Tour step 11
    * @property {string} title - The title of the tour step.
    * @property {string} content - The content or description of the tour step.
    * @property {string} selector - The CSS selector for the element that this step should target.
    * @property {Orientation} orientation - The orientation of the tour step (e.g., Orientation.Top).
    * @property {Function} action - A function to execute when the tour step is reached.
    * @property {Function} closeAction - A function to execute when the tour is closed or completed.
    */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wfrun.wfRunCard.title),
      content: this.i18n.getString(TourGuideContent.workflows.wfrun.wfRunCard.content),
      selector: `#${TourGuideContent.workflows.wfrun.wfRunCard.id}`,
      orientation: Orientation.Top,
    },
    /**Tour step 12 */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wfrun.wfRunCardMenu.title),
      content: this.i18n.getString(TourGuideContent.workflows.wfrun.wfRunCardMenu.content),
      selector: `#${TourGuideContent.workflows.wfrun.wfRunCardMenu.id}`,
      orientation: Orientation.Right,
      highlightPadding: 5,
      action: () => {
        // this.simulateClickOnElement(TourGuideContent.workflows.wfrun.wfRunCardMenu.id);
      }
    },
    // /**
    // * Tour step 13
    // * @property {string} title - The title of the tour step.
    // * @property {string} content - The content or description of the tour step.
    // * @property {string} selector - The CSS selector for the element that this step should target.
    // * @property {Orientation} orientation - The orientation of the tour step (e.g., Orientation.Top).
    // * @property {Function} action - A function to execute when the tour step is reached.
    // * @property {Function} closeAction - A function to execute when the tour is closed or completed.
    // */
    // {
    //   title: this.i18n.getString(TourGuideContent.workflows.wfrun.wfCardMenu.title),
    //   content: this.i18n.getString(TourGuideContent.workflows.wfrun.wfCardMenu.content),
    //   selector: `#${TourGuideContent.workflows.wfrun.wfCardMenu.id}`,
    //   orientation: Orientation.Right,
    //   highlightPadding: 5,
    //   closeAction: () => {
    //     this.simulateClickOnElement(TourGuideContent.workflows.wfrun.wfRunCardMenu.id);
    //   }
    // },
    /** Tour step 14 */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wfrun.wfRunOverview.title),
      content: this.i18n.getString(TourGuideContent.workflows.wfrun.wfRunOverview.content),
      selector: `#${TourGuideContent.workflows.wfrun.wfExpanded.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.dashboardIndex = 0;
      }
    },
    /**
    * Tour step 15
    * @property {string} title - The title of the tour step.
    * @property {string} content - The content or description of the tour step.
    * @property {string} selector - The CSS selector for the element that this step should target.
    * @property {Orientation} orientation - The orientation of the tour step (e.g., Orientation.Top).
    * @property {Function} action - A function to execute when the tour step is reached.
    * @property {Function} closeAction - A function to execute when the tour is closed or completed.
    */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wfrun.wfQualityGates.title),
      content: this.i18n.getString(TourGuideContent.workflows.wfrun.wfQualityGates.content),
      selector: `#${TourGuideContent.workflows.wfrun.wfExpanded.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.dashboardIndex = 1;
      }
    },
    /**
    * Tour step 16
    * @property {string} title - The title of the tour step.
    * @property {string} content - The content or description of the tour step.
    * @property {string} selector - The CSS selector for the element that this step should target.
    * @property {Orientation} orientation - The orientation of the tour step (e.g., Orientation.Right).
    * @property {Function} action - A function to execute when the tour step is reached.
    * @property {Function} closeAction - A function to execute when the tour is closed or completed.
    */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wfrun.wfInputArgs.title),
      content: this.i18n.getString(TourGuideContent.workflows.wfrun.wfInputArgs.content),
      selector: `#${TourGuideContent.workflows.wfrun.wfExpanded.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.dashboardIndex = 2;
      }
    },
    /**
    * Tour step 17
    * @property {string} title - The title of the tour step.
    * @property {string} content - The content or description of the tour step.
    * @property {string} selector - The CSS selector for the element that this step should target.
    * @property {Orientation} orientation - The orientation of the tour step (e.g., Orientation.Right).
    * @property {Function} action - A function to execute when the tour step is reached.
    * @property {Function} closeAction - A function to execute when the tour is closed or completed.
    */
    {
      title: this.i18n.getString(TourGuideContent.workflows.wfrun.wfBack.title),
      content: this.i18n.getString(TourGuideContent.workflows.wfrun.wfBack.content),
      selector: `#${TourGuideContent.workflows.wfrun.wfBack.id}`,
      orientation: Orientation.Right,
      action: () => {
        this.simulateClickOnElement('rerun-wf');
      }
    },
  ]

  /**********  Workflow run tour steps **********/
  wfrunSteps: TourStep[] = [
  ]

  /**********  Admin access tour steps **********/
  accessPageSteps: TourStep[] = [
    /** Tour step 1 */
    {
      title: this.i18n.getString(TourGuideContent.navbarContent.admin.title),
      content: this.i18n.getString(TourGuideContent.navbarContent.admin.content),
      selector: '',
      orientation: Orientation.Center,
    },
    /** Tour step 2 COPY OF wfqueuemanager tour step for dropdown*/
    {
      title: this.i18n.getString(TourGuideContent.featureWfContent.charts.dropdowns.title),
      content: this.i18n.getString(TourGuideContent.featureWfContent.charts.dropdowns.content),
      selector: `#${TourGuideContent.wfQueueMgr.dropDown.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.dataSubject.next({ operation: 'open', page: 'Access' });
        this.tourClass('productTourId', 'wf-configw', 'add');
        this.scrollToView(TourGuideContent.wfQueueMgr.dropDown.id);
      },
      closeAction: () => {
        this.dataSubject.next({ operation: false })
        this.tourClass('productTourId', 'wf-configw', 'remove')
        this.scrollToView(TourGuideContent.featureWfContent.charts.dropdowns.id);
      },
    },
    /** Tour step 2 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.access.access_table.title),
      content: this.i18n.getString(TourGuideContent.adminContent.access.access_table.content),
      selector: `#${TourGuideContent.adminContent.access.access_table.id}`,
      orientation: Orientation.Bottom,
    },
    /** Tour step 3 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.access.removeAssignGrp.title),
      content: this.i18n.getString(TourGuideContent.adminContent.access.removeAssignGrp.content),
      selector: `#${TourGuideContent.adminContent.access.removeAssignGrp.id}`,
      orientation: Orientation.Bottom
    },
    /** Tour step 4 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.access.removeGrpSaveData.title),
      content: this.i18n.getString(TourGuideContent.adminContent.access.removeGrpSaveData.content),
      selector: `#${TourGuideContent.adminContent.access.removeGrpSaveData.id}`,
      orientation: Orientation.TopRight,
      action: () => {
        this.simulateClickOnElement('acc_table_cancel_btn');
      }
    },
    /** Tour step 5 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.access.grpColumnValue.title),
      content: this.i18n.getString(TourGuideContent.adminContent.access.grpColumnValue.content),
      selector: `#${TourGuideContent.adminContent.access.grpColumnValue.id}`,
      orientation: Orientation.Bottom,
      action: () => {
        this.simulateClickOnElement('acc_table_save_btn');
        this.ts.closeToast()
      }
    },
    /** Tour step 6 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.access.addValueAssignGrp.title),
      content: this.i18n.getString(TourGuideContent.adminContent.access.addValueAssignGrp.content),
      selector: `#${TourGuideContent.adminContent.access.addValueAssignGrp.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.simulateSelectFromAutocomplete('acc_table_add_grp_input', 'optimus_gm');
        this.tourClass('productTourId', 'accessGroup', 'add');
        this.dataSubject.next({ access: 'open' });
        this.ts.closeToast()
      },
      closeAction: () => {
        this.tourClass('productTourId', 'accessGroup', 'remove')
        this.dataSubject.next({ access: 'close' });
        this.ts.closeToast()
      },
    },
    /** Tour step 7 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.access.assignGrpSaveData.title),
      content: this.i18n.getString(TourGuideContent.adminContent.access.assignGrpSaveData.content),
      selector: `#${TourGuideContent.adminContent.access.assignGrpSaveData.id}`,
      orientation: Orientation.TopRight,
      action: () => {
        this.dataSubject.next({ operation: 'add' })
      }
    },
    /** Tour step 8 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.access.grpColumnValue.title),
      content: this.i18n.getString(TourGuideContent.adminContent.access.grpColumnValue.content),
      selector: `#${TourGuideContent.adminContent.access.grpColumnValue.id}`,
      orientation: Orientation.Bottom,
      action: () => {
        this.simulateClickOnElement('acc_table_save_btn');
        this.ts.closeToast()
      }
    },
  ]

  /**********  Admin tools tour steps **********/
  tools: TourStep[] = [
    /** Tour step 1 */
    {
      title: this.i18n.getString(TourGuideContent.navbarContent.tools.title),
      content: this.i18n.getString(TourGuideContent.navbarContent.tools.content),
      selector: '',
      orientation: Orientation.Center,
    },
    /** Tour step 2 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.toolsTable.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.toolsTable.content),
      selector: `#${TourGuideContent.adminContent.tools.toolsTable.id}`,
      orientation: Orientation.Top,
    },
    /** Tour step 3 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.refresh.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.refresh.content),
      selector: `#${TourGuideContent.adminContent.tools.refresh.id}`,
      orientation: Orientation.Left,
      highlightPadding: 5,
    },
    /** Tour step 4 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.addTool.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.addTool.content),
      selector: `#${TourGuideContent.adminContent.tools.addTool.id}`,
      orientation: Orientation.Left,
    },
    /** Tour step 5 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.toggleBtn.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.toggleBtn.content),
      selector: `#${TourGuideContent.adminContent.tools.toggleBtn.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.simulateClickOnElement('add-Tool');
      },
      closeAction: () => {
        this.simulateClickOnElement('close-Modal');
      }
    },
    /** Tour step 6 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.primaryInfo.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.primaryInfo.content),
      selector: `#${TourGuideContent.adminContent.tools.primaryInfo.id}`,
      orientation: Orientation.Left,
      highlightPadding: 10,
      action: () => {
        this.simulateClickOnElement('add-Tool');
        this.scrollToView(TourGuideContent.adminContent.tools.primaryInfo.id);
      }
    },
    /** Tour step 7 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.resourceLimit.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.resourceLimit.content),
      selector: `#${TourGuideContent.adminContent.tools.resourceLimit.id}`,
      orientation: Orientation.Left,
      highlightPadding: 10,
      action: () => {
        this.scrollToView(TourGuideContent.adminContent.tools.resourceLimit.id);
      }
    },
    /** Tour step 8 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.qgExpand.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.qgExpand.content),
      selector: `#${TourGuideContent.adminContent.tools.qgExpand.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.simulateClickOnElement('add-Tool');
        this.scrollToView('qualityGates')
        this.simulateClickOnElement('cancelBtn')
      },
      closeAction: () => {
      }
    },
    /** Tour step 9 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.qualityGates.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.qualityGates.content),
      selector: `#${TourGuideContent.adminContent.tools.qualityGates.id}`,
      orientation: Orientation.Top,
      highlightPadding: 13,
      action: () => {
        this.simulateClickOnElement('add-Tool');
        this.scrollToView('qualityGates')
        this.simulateClickOnElement('expand');
        this.simulateClickOnElement('cancelBtn')
      },
    },
    /** Tour step 10 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.qgFields.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.qgFields.content),
      selector: `#${TourGuideContent.adminContent.tools.qgFields.id}`,
      orientation: Orientation.Top,
      highlightPadding: 13,
      action: () => {
        this.simulateClickOnElement('expand');
      },
    },
    /** Tour step 11 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.saveBtn.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.saveBtn.content),
      selector: `#${TourGuideContent.adminContent.tools.saveBtn.id}`,
      orientation: Orientation.Top,
      highlightPadding: 5,
      action: () => {
        this.simulateClickOnElement('expand')
        this.scrollToView('saveBtn')
      },
    },
    /** Tour step 12 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.qualityGatesTbl.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.qualityGatesTbl.content),
      selector: `#${TourGuideContent.adminContent.tools.qualityGatesTbl.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.simulateClickOnElement('add-Tool');
        this.simulateClickOnElement('saveBtn');
        this.scrollToView('qualityGates');
      },
    },
    /** Tour step 13 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.modalSaveBtn.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.modalSaveBtn.content),
      selector: `#${TourGuideContent.adminContent.tools.modalSaveBtn.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.simulateClickOnElement('add-Tool');
        this.scrollToView('modalSaveBtn');
      },
      closeAction: () => {
        this.simulateClickOnElement('close-Modal')
      }
    },
    /** Tour step 14 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.editBtn.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.editBtn.content),
      selector: `#${TourGuideContent.adminContent.tools.editBtn.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.simulateClickOnElement('modalSaveBtn')
        this.ts.showSuccess(Messages.addTool.success, Messages.addTool.toolAdd);
        this.ts.closeToast()
      },
      closeAction: () => {
        this.simulateClickOnElement('add-tool')
      }
    },
    /** Tour step 15 */
    {
      title: this.i18n.getString(TourGuideContent.adminContent.tools.deleteBtn.title),
      content: this.i18n.getString(TourGuideContent.adminContent.tools.deleteBtn.content),
      selector: `#${TourGuideContent.adminContent.tools.deleteBtn.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.ts.showSuccess(Messages.addTool.success, Messages.addTool.toolDel)
        this.ts.closeToast()
      },
    }

  ]

  /************ Help tour steps ************/
  helpSteps: TourStep[] = [
    {
      title: this.i18n.getString(TourGuideContent.helpPage.pages.title),
      content: this.i18n.getString(TourGuideContent.helpPage.pages.content),
      selector: '',
      orientation: Orientation.Right,
    },
    {
      title: this.i18n.getString(TourGuideContent.helpPage.search.title),
      content: this.i18n.getString(TourGuideContent.helpPage.search.content),
      selector: `#${TourGuideContent.helpPage.search.id}`,
      orientation: Orientation.Bottom,
    },
    {
      title: this.i18n.getString(TourGuideContent.helpPage.documents.title),
      content: this.i18n.getString(TourGuideContent.helpPage.documents.content),
      selector: `#${TourGuideContent.helpPage.documents.id}`,
      orientation: Orientation.Right,
    },
    {
      title: this.i18n.getString(TourGuideContent.helpPage.viewMore.title),
      content: this.i18n.getString(TourGuideContent.helpPage.viewMore.content),
      selector: `#${TourGuideContent.helpPage.viewMore.id}`,
      orientation: Orientation.TopRight,
      highlightPadding: 4,
    }
  ]

  /************ Github actions tour steps ************/
  githubActionsSteps: TourStep[] = [
    /** Tour step 1 */
    {
      title: this.i18n.getString(TourGuideContent.navbarContent.githubAction.title),
      content: this.i18n.getString(TourGuideContent.navbarContent.githubAction.content),
      selector: '',
      orientation: Orientation.Center,
    },

    /** Tour step 2 COPY OF wfqueuemanager tour step for dropdown*/
    {
      title: this.i18n.getString(TourGuideContent.featureWfContent.charts.dropdowns.title),
      content: this.i18n.getString(TourGuideContent.featureWfContent.charts.dropdowns.content),
      selector: `#${TourGuideContent.wfQueueMgr.dropDown.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.dataSubject.next({ page: 'gitAction', operation: true });
        this.tourClass('productTourId', 'wf-configw', 'add');
        this.scrollToView(TourGuideContent.wfQueueMgr.dropDown.id);
      },
      closeAction: () => {
        this.dataSubject.next({ operation: false })
        this.tourClass('productTourId', 'wf-configw', 'remove')
        this.scrollToView(TourGuideContent.featureWfContent.charts.dropdowns.id);
      },
    },
    /** Tour step 2 */
    {
      title: this.i18n.getString(TourGuideContent.githubAction.table.title),
      content: this.i18n.getString(TourGuideContent.githubAction.table.content),
      selector: `#${TourGuideContent.githubAction.table.id}`,
      orientation: Orientation.Bottom,
    },
    /** Tour step 3 */
    {
      title: this.i18n.getString(TourGuideContent.githubAction.tableContent.status.title),
      content: this.i18n.getString(TourGuideContent.githubAction.tableContent.status.content),
      selector: `#${TourGuideContent.githubAction.tableContent.status.id}`,
      orientation: Orientation.BottomLeft,
      action: () => {
        this.simulateClickOnElement('wf-cancel-btn');
      }
    },
    /** Tour step 4 */
    {
      title: this.i18n.getString(TourGuideContent.githubAction.tableContent.runWf.title),
      content: this.i18n.getString(TourGuideContent.githubAction.tableContent.runWf.content),
      selector: `#${TourGuideContent.githubAction.tableContent.runWf.id}`,
      orientation: Orientation.Left,
      action: () => {
        this.scrollToView(TourGuideContent.githubAction.tableContent.runWf.id);
        this.simulateClickOnElement('gitActionRunWf');
      }
    },
    /** Tour step 5 */ /**Reusing steps from config builder inputs args, runner details, tools and threshold */
    {
      title: this.i18n.getString(TourGuideContent.githubAction.expand.title),
      content: this.i18n.getString(TourGuideContent.githubAction.expand.content),
      selector: `#${TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.dashboardIndex = 0;
        this.handleClick(true);
        this.tourClass('productTourId', 'tourStepCard', 'add');
      },
      closeAction: () => {
        this.tourClass('productTourId', 'tourStepCard', 'remove');
      }
    },
    // /** Tour step 5 */
    // {
    //   title: this.i18n.getString(TourGuideContent.configBuilder.workflow.runnerDetailEdit.title),
    //   content: this.i18n.getString(TourGuideContent.configBuilder.workflow.runnerDetailEdit.content),
    //   selector: `#${TourGuideContent.configBuilder.workflow.runnerDetailEdit.id}`,
    //   orientation: Orientation.Left,
    //   action: () => {
    //     this.scrollToView(TourGuideContent.configBuilder.workflow.runnerDetailEdit.id);
    //   }
    // },
    // /** Tour step 6 */
    // {
    //   title: this.i18n.getString(TourGuideContent.configBuilder.workflow.runnerDetail.title),
    //   content: this.i18n.getString(TourGuideContent.configBuilder.workflow.runnerDetail.content),
    //   selector: `#${TourGuideContent.configBuilder.workflow.runnerDetail.id}`,
    //   orientation: Orientation.Left,
    //   action: () => {
    //     this.dashboardIndex = 0;
    //     this.simulateClickOnElement(TourGuideContent.configBuilder.workflow.runnerDetailEdit.id)
    //     timer(500).pipe(delay(500)).subscribe(() => {
    //       this.dataSubject.next({ operation: 'open' });
    //     })
    //     timer(300).pipe(delay(300)).subscribe(() => { this.tourClass('productTourId', 'runnerdetail', 'add') })
    //     this.scrollToView(TourGuideContent.configBuilder.workflow.runnerDetailEdit.id);
    //   },
    //   closeAction: () => {
    //     this.simulateClickOnElement(TourGuideContent.clickId)
    //     this.tourClass('productTourId', 'runnerdetail', 'remove');
    //     this.scrollToView(TourGuideContent.configBuilder.workflow.runnerDetailEdit.id)
    //   }
    // },
    /** Tour step 6 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.content),
      selector: `#${TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.simulateClickOnElement(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.id);
        this.scrollToView(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.id)
        this.dashboardIndex = 1;
      },
      closeAction: () => {
        this.simulateClickOnElement(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.table.id);
      }
    },
    /** Tour step 7 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.checkBox.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.checkBox.content),
      selector: `#${TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.checkBox.id}`,
      orientation: Orientation.Top,
      action: () => this.dataSubject.next({ checkBox: true })
    },
    /** Tour step 8 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.condition.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.condition.content),
      selector: `#${TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.condition.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.dataSubject.next({ operation: 'open' })
        this.dataSubject.next({ checkBox: true }),
          this.tourClass('productTourId', 'config-conditionId', 'add')
      },
      closeAction: () => {
        this.dataSubject.next({ operation: 'close' }),
          this.tourClass('productTourId', 'config-conditionId', 'remove')
      },
    },
    /** Tour step 9 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.inputBox.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.inputBox.content),
      selector: `#${TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.inputBox.id}`,
      orientation: Orientation.Top,
      action: () => {
        this.dataSubject.next({ checkBox: true })
      }
    },
    /** Tour step 10 */
    {
      title: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.allFail.title),
      content: this.i18n.getString(TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.allFail.content),
      selector: `#${TourGuideContent.configBuilder.workflow.toolsThreshHoldTab.allFail.id}`,
      orientation: Orientation.Top,
    },
    /** Tour step 11 */
    {
      title: this.i18n.getString(TourGuideContent.githubAction.tableContent.runWf.title),
      content: this.i18n.getString(TourGuideContent.githubAction.expand.run.content),
      selector: `#${TourGuideContent.githubAction.expand.run.id}`,
      orientation: Orientation.TopRight,
      action: () => {
        this.dataSubject.next({ checkBox: true, configSave: true })
        this.scrollToView(TourGuideContent.githubAction.expand.run.id);
      },
    }
  ]

  /************ Dependent Checks tour steps ************/
  dependentChecksSteps: TourStep[] = [
    /** Tour step 1 - Introduction */
    {
      title: this.i18n.getString(TourGuideContent.navbarContent.dependentChecks.title),
      content: this.i18n.getString(TourGuideContent.navbarContent.dependentChecks.content),
      selector: '',
      orientation: Orientation.Center,
    },
    /** Tour step 2 - Configuration Table */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.configTable.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.configTable.content),
      selector: TourGuideContent.dependentChecks.configTable.selector,
      orientation: Orientation.Top,
    },
    /** Tour step 3 - Expand Button */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.expandBtn.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.expandBtn.content),
      selector: TourGuideContent.dependentChecks.expandBtn.selector,
      orientation: Orientation.Right,
    },
    /** Tour step 4 - Status Toggle */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.statusToggle.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.statusToggle.content),
      selector: TourGuideContent.dependentChecks.statusToggle.selector,
      orientation: Orientation.Left,
    },
    /** Tour step 5 - Edit Button */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.editBtn.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.editBtn.content),
      selector: TourGuideContent.dependentChecks.editBtn.selector,
      orientation: Orientation.Left,
    },
    /** Tour step 6 - Delete Button */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.deleteBtn.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.deleteBtn.content),
      selector: TourGuideContent.dependentChecks.deleteBtn.selector,
      orientation: Orientation.Left,
    },
    /** Tour step 7 - Add Button */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.addBtn.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.addBtn.content),
      selector: TourGuideContent.dependentChecks.addBtn.selector,
      orientation: Orientation.Left,
      action: () => {
        // Close form when navigating backward to step 7
        // Component subscribes to data$ and handles the UI transition
        this.dataSubject.next({ cancelConfig: true });
      }
    },
    /** Tour step 8 - Configuration Name */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.configName.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.configName.content),
      selector: TourGuideContent.dependentChecks.configName.selector,
      orientation: Orientation.Bottom,
      highlightPadding: 5,
      action: () => {
        // Open form when navigating forward to step 8
        // Component subscribes to data$ and handles the UI transition
        this.dataSubject.next({ openAddConfig: true });
      }
    },
    /** Tour step 9 - Organization */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.orgField.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.orgField.content),
      selector: TourGuideContent.dependentChecks.orgField.selector,
      orientation: Orientation.Bottom,
      highlightPadding: 5,
    },
    /** Tour step 10 - Repository Pattern */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.repoPattern.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.repoPattern.content),
      selector: TourGuideContent.dependentChecks.repoPattern.selector,
      orientation: Orientation.Bottom,
      highlightPadding: 5,
    },
    /** Tour step 11 - Branch Pattern */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.branchField.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.branchField.content),
      selector: TourGuideContent.dependentChecks.branchField.selector,
      orientation: Orientation.Bottom,
      highlightPadding: 5,
    },
    /** Tour step 12 - Event Dropdown */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.eventDropdown.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.eventDropdown.content),
      selector: TourGuideContent.dependentChecks.eventDropdown.selector,
      orientation: Orientation.Bottom,
      highlightPadding: 5,
    },
    /** Tour step 13 - Action Dropdown */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.actionDropdown.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.actionDropdown.content),
      selector: TourGuideContent.dependentChecks.actionDropdown.selector,
      orientation: Orientation.Bottom,
      highlightPadding: 5,
    },
    /** Tour step 14 - Configurations Card */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.configurationsCard.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.configurationsCard.content),
      selector: TourGuideContent.dependentChecks.configurationsCard.selector,
      orientation: Orientation.Right,
    },
    /** Tour step 15 - Dependency Graph */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.dependencyGraph.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.dependencyGraph.content),
      selector: TourGuideContent.dependentChecks.dependencyGraph.selector,
      orientation: Orientation.Left,
    },
    /** Tour step 16 - Cancel Button */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.cancelBtn.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.cancelBtn.content),
      selector: TourGuideContent.dependentChecks.cancelBtn.selector,
      orientation: Orientation.Left,
    },
    /** Tour step 17 - Save Button */
    {
      title: this.i18n.getString(TourGuideContent.dependentChecks.saveBtn.title),
      content: this.i18n.getString(TourGuideContent.dependentChecks.saveBtn.content),
      selector: TourGuideContent.dependentChecks.saveBtn.selector,
      orientation: Orientation.Left,
    }
  ]

  /************ Links tour steps ************/
  linksSteps: TourStep[] = [
    /** Tour step 1 */
    {
      title: this.i18n.getString(TourGuideContent.navbarContent.links.title),
      content: this.i18n.getString(TourGuideContent.navbarContent.links.content),
      selector: '',
      orientation: Orientation.Center,
      action: () => {
        this.simulateClickOnElement(TourGuideContent.linksPage.linksCancelClick.id);
      }
    },
    /** Tour step 2 */
    {
      title: this.i18n.getString(TourGuideContent.linksPage.linksAdd.title),
      content: this.i18n.getString(TourGuideContent.linksPage.linksAdd.content),
      selector: `#${TourGuideContent.linksPage.linksAdd.id}`,
      orientation: Orientation.Left,
      highlightPadding: 8,
      action: ()=> {
        this.dataSubject.next({ addMode: false })
        this.ts.closeToast();
      }
    },
    /** Tour step 3 */
    {
      title: this.i18n.getString(TourGuideContent.linksPage.linksAdd.linksAddBlock.title),
      content: this.i18n.getString(TourGuideContent.linksPage.linksAdd.linksAddBlock.content),
      selector: `#${TourGuideContent.linksPage.linksAdd.linksAddBlock.id}`,
      orientation: Orientation.Top,
      highlightPadding: 7,
      action: () => {
        this.dataSubject.next({ addMode: true })
        this.dataSubject.next({ dummyData: true })
        this.tourClass('productTourId', 'tourStepCard', 'add')
      },
      closeAction: () => {
        this.tourClass('productTourId', 'tourStepCard', 'remove')
      }
    },
    /** Tour step 4 */
    {
      title: this.i18n.getString(TourGuideContent.linksPage.linksAdd.linksAddStatus.title),
      content: this.i18n.getString(TourGuideContent.linksPage.linksAdd.linksAddStatus.content),
      selector: `#${TourGuideContent.linksPage.linksAdd.linksAddStatus.id}`,
      orientation: Orientation.Bottom,
      highlightPadding: 5,
    },
    /** Tour step 5 */
    {
      title: this.i18n.getString(TourGuideContent.linksPage.linksAdd.linksAddSave.title),
      content: this.i18n.getString(TourGuideContent.linksPage.linksAdd.linksAddSave.content),
      selector: `#${TourGuideContent.linksPage.linksAdd.linksAddSave.id}`,
      orientation: Orientation.Left,
      highlightPadding: 8,
      action: () => {
        this.dataSubject.next({ addMode: true })
        this.dataSubject.next({ dummyData: true });
        this.scrollToView(TourGuideContent.linksPage.linksAdd.linksAddBlock.id);
      },
    },
    /** Tour step 6 */
    {
      title: this.i18n.getString(TourGuideContent.linksPage.linksSearch.title),
      content: this.i18n.getString(TourGuideContent.linksPage.linksSearch.content),
      selector: `#${TourGuideContent.linksPage.linksSearch.id}`,
      orientation: Orientation.Left,
      highlightPadding: 5,
      action: () => {
        this.dataSubject.next({ addMode: false })
        this.ts.closeToast();
        this.scrollToView(TourGuideContent.linksPage.linksSearch.id);
      },
    },
    /** Tour step 7 */
    {
      title: this.i18n.getString(TourGuideContent.linksPage.linksTable.linksTableInfo.title),
      content: this.i18n.getString(TourGuideContent.linksPage.linksTable.linksTableInfo.content),
      selector: `#${TourGuideContent.linksPage.linksTable.linksTableInfo.id}`,
      orientation: Orientation.Top,
      highlightPadding: 5,
    },
    /** Tour step 8 */
    {
      title: this.i18n.getString(TourGuideContent.linksPage.linksTable.linksTableEdit.title),
      content: this.i18n.getString(TourGuideContent.linksPage.linksTable.linksTableEdit.content),
      selector: `#${TourGuideContent.linksPage.linksTable.linksTableEdit.id}`,
      orientation: Orientation.TopRight,
    },
    /** Tour step 9 */
    {
      title: this.i18n.getString(TourGuideContent.linksPage.linksTable.linksTableDelete.title),
      content: this.i18n.getString(TourGuideContent.linksPage.linksTable.linksTableDelete.content),
      selector: `#${TourGuideContent.linksPage.linksTable.linksTableDelete.id}`,
      orientation: Orientation.TopRight,
    },
  ]

  /************ GitHub Sync tour steps ************/
  githubSyncSteps: TourStep[] = [
    /** Tour step 1 - Page Introduction */
    {
      title: this.i18n.getString(TourGuideContent.navbarContent.githubSync.title),
      content: this.i18n.getString(TourGuideContent.navbarContent.githubSync.content),
      selector: '',
      orientation: Orientation.Center,
    },
    /** Tour step 2 - Organization Dropdown */
    {
      title: this.i18n.getString(TourGuideContent.githubSync.orgDropdown.title),
      content: this.i18n.getString(TourGuideContent.githubSync.orgDropdown.content),
      selector: `#${TourGuideContent.githubSync.orgDropdown.id}`,
      orientation: Orientation.Bottom,
    },
    /** Tour step 3 - Repository Dropdown */
    {
      title: this.i18n.getString(TourGuideContent.githubSync.repoDropdown.title),
      content: this.i18n.getString(TourGuideContent.githubSync.repoDropdown.content),
      selector: `#${TourGuideContent.githubSync.repoDropdown.id}`,
      orientation: Orientation.Bottom,
    },
    /** Tour step 4 - Branch Dropdown */
    {
      title: this.i18n.getString(TourGuideContent.githubSync.branchDropdown.title),
      content: this.i18n.getString(TourGuideContent.githubSync.branchDropdown.content),
      selector: `#${TourGuideContent.githubSync.branchDropdown.id}`,
      orientation: Orientation.Bottom,
    },
    /** Tour step 5 - Issue Templates Table explanation */
    {
      title: this.i18n.getString(TourGuideContent.githubSync.table.title),
      content: this.i18n.getString(TourGuideContent.githubSync.table.content),
      selector: `#${TourGuideContent.githubSync.table.id}`,
      orientation: Orientation.Top,
      action: () => this.dataSubject.next({ operation: 'loadIssueTemplates' })
    },
    /** Tour step 6 - Checkbox Selection */
    {
      title: this.i18n.getString(TourGuideContent.githubSync.checkboxSelection.title),
      content: this.i18n.getString(TourGuideContent.githubSync.checkboxSelection.content),
      selector: TourGuideContent.githubSync.checkboxSelection.selector,
      orientation: Orientation.Right,
      action: () => this.dataSubject.next({ operation: 'selectCheckboxes' })
    },
    /** Tour step 7 - Synchronize Button */
    {
      title: this.i18n.getString(TourGuideContent.githubSync.syncButton.title),
      content: this.i18n.getString(TourGuideContent.githubSync.syncButton.content),
      selector: `#${TourGuideContent.githubSync.syncButton.id}`,
      orientation: Orientation.TopRight,
    },
  ]

  /************ PR Grouping tour steps ************/
  prGroupingSteps: TourStep[] = [
    /** Tour step 1 - Page Introduction */
    {
      title: this.i18n.getString(TourGuideContent.navbarContent.prGrouping.title),
      content: this.i18n.getString(TourGuideContent.navbarContent.prGrouping.content),
      selector: '',
      orientation: Orientation.Center,
    },
    /** Tour step 2 - Topic search */
    {
      title: this.i18n.getString(TourGuideContent.prGrouping.topicField.title),
      content: this.i18n.getString(TourGuideContent.prGrouping.topicField.content),
      selector: TourGuideContent.prGrouping.topicField.selector,
      orientation: Orientation.Bottom,
      highlightPadding: 5,
    },
    /** Tour step 3 - Repo search */
    {
      title: this.i18n.getString(TourGuideContent.prGrouping.repoField.title),
      content: this.i18n.getString(TourGuideContent.prGrouping.repoField.content),
      selector: TourGuideContent.prGrouping.repoField.selector,
      orientation: Orientation.Bottom,
      highlightPadding: 5,
    },
    /** Tour step 4 - PR URL */
    {
      title: this.i18n.getString(TourGuideContent.prGrouping.prUrlField.title),
      content: this.i18n.getString(TourGuideContent.prGrouping.prUrlField.content),
      selector: TourGuideContent.prGrouping.prUrlField.selector,
      orientation: Orientation.Bottom,
      highlightPadding: 5,
    },
    /** Tour step 5 - Date range */
    {
      title: this.i18n.getString(TourGuideContent.prGrouping.dateRange.title),
      content: this.i18n.getString(TourGuideContent.prGrouping.dateRange.content),
      selector: TourGuideContent.prGrouping.dateRange.selector,
      orientation: Orientation.Bottom,
      highlightPadding: 5,
    },
    /** Tour step 6 - All Inputs */
    {
      title: this.i18n.getString(TourGuideContent.prGrouping.searchBtn.title),
      content: this.i18n.getString(TourGuideContent.prGrouping.searchBtn.content),
      selector: TourGuideContent.prGrouping.searchBtn.selector,
      orientation: Orientation.Bottom,
      highlightPadding: 5,
      action: () => this.dataSubject.next({ prgEditMode: true }),
    },
    /** Tour step 7 - Search button */
    {
      title: this.i18n.getString(TourGuideContent.prGrouping.searchBtnOnly.title),
      content: this.i18n.getString(TourGuideContent.prGrouping.searchBtnOnly.content),
      selector: TourGuideContent.prGrouping.searchBtnOnly.selector,
      orientation: Orientation.Left,
      highlightPadding: 5,
      action: () => this.dataSubject.next({ prgEditMode: true }),
    },
    /** Tour step 8 - Edit button */
    {
      title: this.i18n.getString(TourGuideContent.prGrouping.editBtn.title),
      content: this.i18n.getString(TourGuideContent.prGrouping.editBtn.content),
      selector: TourGuideContent.prGrouping.editBtn.selector,
      orientation: Orientation.Left,
      highlightPadding: 5,
      action: () => this.dataSubject.next({ prgReadOnly: true }),
      closeAction: () => this.dataSubject.next({ prgEditMode: true }),
    },
    /** Tour step 9 - Results table */
    {
      title: this.i18n.getString(TourGuideContent.prGrouping.resultsTable.title),
      content: this.i18n.getString(TourGuideContent.prGrouping.resultsTable.content),
      selector: TourGuideContent.prGrouping.resultsTable.selector,
      orientation: Orientation.Top,
      action: () => this.dataSubject.next({ prgReadOnly: true }),
    },
  ]

  /************ Job Executions (Scheduler) tour steps ************/
  jobExecutionsSteps: TourStep[] = [
    /** Tour step 1 - Page Introduction */
    {
      title: this.i18n.getString(TourGuideContent.navbarContent.jobExecutions.title),
      content: this.i18n.getString(TourGuideContent.navbarContent.jobExecutions.content),
      selector: '',
      orientation: Orientation.Center,
    },
    /** Tour step 2 - Date picker */
    {
      title: this.i18n.getString(TourGuideContent.jobExecutions.datePicker.title),
      content: this.i18n.getString(TourGuideContent.jobExecutions.datePicker.content),
      selector: TourGuideContent.jobExecutions.datePicker.selector,
      orientation: Orientation.BottomRight,
      highlightPadding: 5,
    },
    /** Tour step 3 - Refresh */
    {
      title: this.i18n.getString(TourGuideContent.jobExecutions.refresh.title),
      content: this.i18n.getString(TourGuideContent.jobExecutions.refresh.content),
      selector: TourGuideContent.jobExecutions.refresh.selector,
      orientation: Orientation.BottomRight,
      highlightPadding: 5,
    },
    /** Tour step 4 - Executions table */
    {
      title: this.i18n.getString(TourGuideContent.jobExecutions.table.title),
      content: this.i18n.getString(TourGuideContent.jobExecutions.table.content),
      selector: TourGuideContent.jobExecutions.table.selector,
      orientation: Orientation.Top,
    },
    /** Tour step 5 - Cancel action */
    {
      title: this.i18n.getString(TourGuideContent.jobExecutions.cancel.title),
      content: this.i18n.getString(TourGuideContent.jobExecutions.cancel.content),
      selector: TourGuideContent.jobExecutions.cancel.selector,
      orientation: Orientation.Left,
      highlightPadding: 5,
    },
    /** Tour step 6 - Builds of a run (the page expands a sample run) */
    {
      title: this.i18n.getString(TourGuideContent.jobExecutions.builds.title),
      content: this.i18n.getString(TourGuideContent.jobExecutions.builds.content),
      selector: TourGuideContent.jobExecutions.builds.selector,
      orientation: Orientation.Top,
      action: () => this.dataSubject.next({ operation: 'jobExecExpandRun' }),
    },
    /** Tour step 7 - Workflows of a build (the page expands a sample build) */
    {
      title: this.i18n.getString(TourGuideContent.jobExecutions.workflows.title),
      content: this.i18n.getString(TourGuideContent.jobExecutions.workflows.content),
      selector: TourGuideContent.jobExecutions.workflows.selector,
      orientation: Orientation.Top,
      action: () => this.dataSubject.next({ operation: 'jobExecExpandBuild' }),
    },
  ]

  /**
   * Start page tour function is used to trigger the page tour along with dummy data
   * @param isTour 
   */
  startPageTour(isTour: boolean) {
    let rout = '';
    let steps: TourStep[] = [];
    const currentUrl = this.router.url.split('/')[1];
    switch (currentUrl) {
      case 'admin': rout = '/admin'; steps = this.accessPageSteps; break;
      case 'dashboard': rout = '/dashboard'; steps = this.dashboardTab == 'KPI Dashboard' ? this.kpiDashboardSteps : this.dashboardTab == 'Distribution' ? this.distributionSteps : this.dashboardTab == 'Summary' ? this.summarySteps : this.dashboardTab == 'Licenses' ? this.licenseSteps : this.dashboardTab == 'Error Diagnostics' ? this.errorDiagnosticsSteps : []; break;
      case 'feature-workflow': rout = '/feature-workflow'; steps = this.featureWfSteps; break;
      case 'queue-mngr': rout = '/queue-mngr'; steps = this.wfQueueMgrSteps; break;
      case 'workflow-config': rout = '/workflow-config'; steps = this.configBuilderWfSteps; break;
      case 'dependent-checks': rout = '/dependent-checks'; steps = this.dependentChecksSteps; break;
      case 'integrated-workflows': rout = '/integrated-workflows'; break;
      case 'github-actions': rout = '/github-actions'; steps = this.githubActionsSteps; break;
      case 'workflows': rout = '/workflows'; steps = this.workflowsSteps; break;
      case 'workflow-run': rout = '/workflows'; steps = this.workflowsSteps; break;
      case 'tools': rout = '/tools'; steps = this.tools; break;
      case 'links': rout = '/links'; steps = this.linksSteps; break;
      case 'help-page': rout = '/help-page'; steps = this.helpSteps; break;
      case 'github-sync': rout = '/github-sync'; steps = this.githubSyncSteps; break;
      case 'pr-grouping': rout = '/pr-grouping'; steps = this.prGroupingSteps; break;
      case 'scheduler':
        if (this.router.url.startsWith('/scheduler/job-executions')) { rout = '/scheduler/job-executions'; steps = this.jobExecutionsSteps; }
        break;
    }


    /**
    * Navigates to a specified route while maintaining the browser's navigation history.
    * If a route is provided, it also triggers a tour if necessary.
    * @param {string} rout - The route to navigate to.
    * @param {boolean} isTour - A flag indicating whether to trigger a tour.
    * @param {TourStep[]} steps - An array of tour steps to be used in the tour.
    */
    if (rout) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([rout]);
      }).then(() => {
        this.isStartTour = isTour;
        if (rout && isTour) {
          this.triggerStartTour(steps);
        }
      });
    }
  }


  /**
   * Simulates a click event on a DOM element with the specified HTML ID.
   * @param {string} htmlId - The HTML ID of the element to simulate a click on.
   */
  simulateClickOnElement(htmlId: string) {
    // Get the DOM element with the specified HTML ID.
    const elementToClick = document.getElementById(htmlId) as HTMLElement;

    // Check if the element exists.
    if (elementToClick) {
      // Trigger a click event on the element.
      elementToClick.click();
    }
  }


  /**
   * Scrolls the viewport to bring a specific HTML element into view smoothly.
   * @param {string} htmlId - The HTML ID of the element to scroll to.
   */
  scrollToView(htmlId: string) {
    // Get the DOM element with the specified HTML ID.
    const element = document.getElementById(htmlId) as HTMLElement;

    // Check if the element exists.
    if (element) {
      // Scroll the viewport to bring the element into view smoothly.
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  }

  /**
   * Simulates selecting an option from an autocomplete input field.
   * @param {string} htmlId - The HTML ID of the autocomplete input field.
   * @param {string} optionValue - The value of the option to select.
   */
  simulateSelectFromAutocomplete(htmlId: string, optionValue: string) {
    const inputElement = document.getElementById(htmlId) as HTMLInputElement;

    if (inputElement) {
      inputElement.click();
      inputElement.value = optionValue;

      const inputEvent = new Event('input', { bubbles: true });
      inputElement.dispatchEvent(inputEvent);

      const arrowDownEvent = new KeyboardEvent('keydown', { key: 'ArrowDown' });
      const enterEvent = new KeyboardEvent('keydown', { key: 'Enter' });

      inputElement.dispatchEvent(arrowDownEvent);
      inputElement.dispatchEvent(enterEvent);
    }

  }

  /**
   * Add class dynamically to tour guide popup
   * @param htmlId : id of the element 
   * @param className : class that needs to be applied on the popup
   * @param op : operation that specifies the adding and removal of the class 
   */
  tourClass(htmlId: any, className: string, op: string) {
    const tourElement = document.getElementById(htmlId) as HTMLElement;
    if (op === 'add') {
      tourElement.classList.add(className)
    }

    if (op == 'remove') {
      tourElement.classList.remove(tourElement.classList[0])
    }
  }

  /**handleClick is used to keep a track of click element */
  handleClick(value: boolean) {
    if (!this.clickTriggered) {
      this.clickTriggered = true;
    } else if (value === false) {
      this.clickTriggered = false;
    }
  }

}
